import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface StatisticsCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  trend?: number;
  color: 'blue' | 'green' | 'yellow' | 'red';
  formatValue?: (value: number) => string;
}

const colorClasses = {
  blue: {
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    icon: 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400',
    value: 'text-blue-600 dark:text-blue-400',
    trendUp: 'text-green-600 dark:text-green-400',
    trendDown: 'text-red-600 dark:text-red-400'
  },
  green: {
    bg: 'bg-green-50 dark:bg-green-900/20',
    icon: 'bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400',
    value: 'text-green-600 dark:text-green-400',
    trendUp: 'text-green-600 dark:text-green-400',
    trendDown: 'text-red-600 dark:text-red-400'
  },
  yellow: {
    bg: 'bg-yellow-50 dark:bg-yellow-900/20',
    icon: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-600 dark:text-yellow-400',
    value: 'text-yellow-600 dark:text-yellow-400',
    trendUp: 'text-green-600 dark:text-green-400',
    trendDown: 'text-red-600 dark:text-red-400'
  },
  red: {
    bg: 'bg-red-50 dark:bg-red-900/20',
    icon: 'bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400',
    value: 'text-red-600 dark:text-red-400',
    trendUp: 'text-green-600 dark:text-green-400', // For overdue, going down is good
    trendDown: 'text-red-600 dark:text-red-400'   // For overdue, going up is bad
  }
};

export const StatisticsCard: React.FC<StatisticsCardProps> = ({
  title,
  value,
  icon,
  trend,
  color,
  formatValue = (v) => v.toLocaleString()
}) => {
  const colors = colorClasses[color];
  
  const renderTrend = () => {
    if (trend === undefined || trend === 0) {
      return (
        <div className="flex items-center gap-1 text-gray-500 text-sm">
          <Minus className="w-4 h-4" />
          <span>0%</span>
        </div>
      );
    }
    
    // For overdue tasks, down trend is good (green), up trend is bad (red)
    // For other metrics, up trend is good, down trend is bad
    const isPositive = color === 'red' ? trend < 0 : trend > 0;
    
    return (
      <div className={`flex items-center gap-1 text-sm ${isPositive ? colors.trendUp : colors.trendDown}`}>
        {trend > 0 ? (
          <TrendingUp className="w-4 h-4" />
        ) : (
          <TrendingDown className="w-4 h-4" />
        )}
        <span>{Math.abs(trend)}%</span>
      </div>
    );
  };

  return (
    <div className={`${colors.bg} rounded-xl p-4 transition-all hover:shadow-md`}>
      <div className="flex items-start justify-between">
        <div className={`${colors.icon} p-2 rounded-lg`}>
          {icon}
        </div>
        {renderTrend()}
      </div>
      <div className="mt-3">
        <p className={`text-xl sm:text-2xl font-bold ${colors.value}`}>
          {formatValue(value)}
        </p>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          {title}
        </p>
      </div>
    </div>
  );
};

export default StatisticsCard;